/**
 * What a plan comes to today: the backend's own quote.
 *
 * `POST /billing/quote/` with the plan's code — and nothing else — and it
 * answers with the whole breakdown: base, discount, taxable amount, the tax
 * split three ways, the rate and the total.
 *
 * The browser works none of that out. Three reasons, and each of them is a way
 * the old arithmetic could have been wrong:
 *
 *   - **A discount is worth what the server says.** The checkout used to price
 *     a coupon against a list it shipped with; the two could disagree, and the
 *     one the buyer read was the one that did not count. No code is sent at all
 *     now, so any discount on the breakdown is the server's own.
 *   - **The tax follows the place of supply**, which comes off the billing
 *     profile — CGST and SGST within the state, IGST across it. Nothing on the
 *     checkout screen knows that.
 *   - **The rate is not ours to hold.** A GST rate compiled into a bundle is a
 *     rate somebody is still being quoted from cache the week after a budget.
 *
 * What the browser still does is the calendar: the quote prices a term but says
 * nothing about when it renews, so `renewalOf` dates that from the cycle.
 */

import { api, API_ENDPOINTS } from "../../../lib/api";
import type { GetToken } from "../../auth/types/auth.types";
import { renewalOf, MONTHS_PER_CYCLE } from "../pricing";
import type {
  AppliedCoupon,
  BillingCycleId,
  PriceBreakdown,
  QuoteRequest,
  QuoteResponse,
} from "../types/billing.types";

/** Decimal string to number, defended: an absent or unparseable figure lands on
 *  0 rather than painting "₹ NaN" into the summary. */
const toAmount = (raw: string | null | undefined): number => {
  const value = Number.parseFloat(raw ?? "");
  return Number.isFinite(value) ? value : 0;
};

/** Money, to the paisa — the sum of three tax lines is otherwise 35.819999. */
const round2 = (amount: number): number => Math.round(amount * 100) / 100;

/** What the checkout asks for and what it draws from the answer. */
export interface QuotedPrice {
  price: PriceBreakdown;
  /**
   * The coupon on the order, or `null` — which is nearly always.
   *
   * Read off the answer, and it can only come from there: nothing sends a code,
   * so this is set when the server applied something of its own accord. A quote
   * that comes back with no code on it is an order with no coupon on it,
   * whatever is sitting in the checkout's field.
   */
  coupon: AppliedCoupon | null;
  /** `"INR"`. Carried so a currency the app cannot write is at least visible to
   *  whoever is debugging it, never to be printed — see `formatMoney`. */
  currency: string;
}

export interface QuoteAsk {
  /** The plan's own code, off the price list: `PRO_MONTHLY`. */
  planCode: string;
  /** Which cycle that code is, for the dates the quote does not carry. */
  cycle: BillingCycleId;
  /** The day the cycle is dated from. Today, except in a test. */
  from?: Date;
}

/** One answer, as the summary reads it. */
export const toQuotedPrice = (
  response: QuoteResponse,
  { cycle, from }: Pick<QuoteAsk, "cycle" | "from">
): QuotedPrice => {
  const discount = toAmount(response.discount_amount);
  // Summed rather than shown three ways: the summary has one GST line, because
  // how the tax is split between the state and the centre changes nothing about
  // what is being paid. The split is the invoice's business, and the invoice is
  // rendered by the backend that made it.
  const tax = round2(
    toAmount(response.cgst_amount) +
      toAmount(response.sgst_amount) +
      toAmount(response.igst_amount)
  );

  return {
    price: {
      months: MONTHS_PER_CYCLE[cycle],
      subtotal: toAmount(response.base_amount),
      discount,
      // Percent on the wire, a fraction on this side — the summary writes it
      // back out as "GST 18%".
      taxRate: toAmount(response.tax_rate_percent) / 100,
      taxableAmount: toAmount(response.taxable_amount),
      tax,
      total: toAmount(response.total_amount),
      renewsOn: renewalOf(cycle, from),
    },
    coupon: response.coupon_code
      ? { code: response.coupon_code, discount }
      : null,
    currency: response.currency,
  };
};

/**
 * Prices a plan.
 *
 * The plan code is the whole of the request. Nothing here decides what an order
 * costs or what any discount on it is worth; the screen's job is to ask and to
 * report the answer, and a refusal comes back as an `ApiError` carrying the
 * reason the server gave.
 */
export async function fetchQuote(
  getToken: GetToken,
  { planCode, cycle, from }: QuoteAsk,
  signal?: AbortSignal
): Promise<QuotedPrice> {
  const body: QuoteRequest = { plan_code: planCode };

  const response = await api.post<QuoteResponse>(
    API_ENDPOINTS.billing.quote,
    body,
    { token: await getToken(), signal }
  );

  return toQuotedPrice(response, { cycle, from });
}
